import React, { useState } from "react";
import { Organization } from "../../models/interfaces/organization";
import CancellationDialogBox from "../cancellation-dialog-box/cancellation-dialog-box";
import OrganizationService from "../../utilities/services/organization-service";
import OrganizationSubscriptionService from "../../utilities/services/organization-subscription-service";
import { UserLimitPlanType } from "../../models/enumerations/user-limit";
import { LicenseLimitPlanType } from "../../models/enumerations/license-limit";
import { ReactComponent as GreenCheckMark } from "../../assets/icons/outline/gree-check-mark.svg";
import { ReactComponent as RedCross } from "../../assets/icons/outline/red-cross.svg";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import ReactivateSubscriptionForm from "./reactivate-subscription-form";
import { useTranslation } from 'react-i18next';
import getFirebase from "../../utilities/firebase";
import { useAuthState } from "../../utilities/contexts/auth-state-context";
import BillingService from "../../utilities/services/billing-service";

export interface ReactivatePlanContentProps {
    COMPONENT_CLASS: string;
    organization: Organization;
    wasAddOnCancelled: boolean;
    products: any[];
}

/**
 * Content for the modal when the user's plan subscription was cancelled due to a failed payment.
 * 
 * @param props The properties
 * @returns {JSX.Element} The component
 */
const ReactivatePlanContent: React.FC<ReactivatePlanContentProps> = (props) => {
    const {
        COMPONENT_CLASS,
        organization,
        wasAddOnCancelled,
        products,
    } = props;
    const { auth } = getFirebase();
    const { state, setState } = useAuthState();
    const { t } = useTranslation();
    const [openCancellationQuestionnaire, setOpenCancellationQuestionnaire] = useState(false);
    const [openPaymentForm, setOpenPaymentForm] = useState(false);
    const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY as string);

    const handleCancelButtonClick = async () => {
        if (organization.id && organization.plan && organization.plan.value !== undefined) {
            const orgSubArr = await OrganizationSubscriptionService.getByOrganizationId(organization.id);

            if (orgSubArr && orgSubArr[0]) {
                const orgSub = orgSubArr[0];

                if (orgSub.stripeAddOnSubscription) {
                    await BillingService.cancelSubscription({
                        subscriptionId: orgSub.stripeAddOnSubscription,
                        email: state.user?.email ?? '',
                    });

                    const planType = organization.plan.value;

                    organization.userLimit = UserLimitPlanType[planType];
                    organization.mySymmioLicense = LicenseLimitPlanType[planType];
                    orgSub.userLimit = UserLimitPlanType[planType];
                    orgSub.mySymmioLicense = LicenseLimitPlanType[planType];
                    orgSub.stripeAddOnSubscription = ""

                    await OrganizationService.save(organization);
                    await OrganizationSubscriptionService.save(orgSub);
                }
            }
        }
        auth.signOut();
        setState((state) => ({ ...state, ...{ authenticated: false, user: null } }));
    }

    return (
        <>
            {openPaymentForm
                ?
                <Elements stripe={stripePromise} options={{ mode: 'setup', currency: 'usd' }}>
                    <ReactivateSubscriptionForm
                        returnUrl="/dashboard?subscriptionStatusModal=showReactivatedPlan"
                        organization={organization}
                        products={products}
                        isReactivatingPlan={true}
                        isReactivatingAddOn={wasAddOnCancelled}
                        onBackButtonClick={() => setOpenPaymentForm(false)}
                    />
                </Elements>
                :
                <>
                    <div className={`${COMPONENT_CLASS}__content-container`}>
                        <h1>{t('reactivateSubscription.updateTitle')}</h1>
                        <p>{t('reactivateSubscription.update_desc')}</p>
                        <div className={`${COMPONENT_CLASS}__actions`}>
                            <div className={`${COMPONENT_CLASS}__clickable`} onClick={() => setOpenPaymentForm(true)}>
                                <div className={`${COMPONENT_CLASS}__image-container`}>
                                    <GreenCheckMark />
                                </div>
                                <div className={`${COMPONENT_CLASS}__text-container`}>
                                    <h4>{t('reactivateSubscription.continueTitle')}</h4>
                                    <p>{t('reactivateSubscription.continue_desc')}</p>
                                </div>
                            </div>

                            <div className={`${COMPONENT_CLASS}__clickable`} onClick={() => setOpenCancellationQuestionnaire(true)}>
                                <div className={`${COMPONENT_CLASS}__image-container`}>
                                    <RedCross />
                                </div>
                                <div className={`${COMPONENT_CLASS}__text-container`}>
                                    <h4>{t('reactivateSubscription.cancelTitle')}</h4>
                                    <p>{t('reactivateSubscription.cancel_desc')}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {openCancellationQuestionnaire &&
                        <CancellationDialogBox
                            isOpen={openCancellationQuestionnaire}
                            onClose={setOpenCancellationQuestionnaire}
                            title={t('billing.cancelTitle')}
                            cancelButtonAction={handleCancelButtonClick}
                        />
                    }
                </>
            }
        </>
    );
};

export default ReactivatePlanContent;